import { WorkflowExecution, WorkflowsService} from '@/api/';
import { useStore } from '@nanostores/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import Breadcrumb from '@/components/breadcrumb';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useState } from 'react';

export type ExecutionDetailsProps = { 
  workflowId: string;
}

export const ExecutionDetails = ({ workflowId }: ExecutionDetailsProps) => {
  const org = useStore($org);
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const workflowExecutions = useQuery({
    queryKey: ['getWorkflowExecutions', workflowId],
    enabled: !!workflowId,
    queryFn: async () => {
      const res = await WorkflowsService.getWorkflowExecutions(workflowId ?? '');
      return res;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  });

  const sortedWorkflowExecutions = Array.isArray(workflowExecutions.data)
    ? workflowExecutions.data
        .slice()
        .sort((a: WorkflowExecution, b: WorkflowExecution) =>
          new Date(b.createdAtUtc).getTime() - new Date(a.createdAtUtc).getTime()
        )
    : [];

  // Calculate pagination
  const totalPages = Math.ceil(sortedWorkflowExecutions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentItems = sortedWorkflowExecutions.slice(startIndex, endIndex);

  const formatDateString = (date: string | Date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  function getDateRangeString(
    startDate: string | Date,
    endDate: string | Date,
  ) {
    const startDateString = formatDateString(startDate);
    const endDateString = formatDateString(endDate);

    if (startDateString === endDateString) {
      return `for ${startDateString}`;
    } else {
      return `between ${startDateString} and ${endDateString}`;
    }
  }

  const workflow = useQuery({
    queryKey: ['getWorkflow', workflowId],
    enabled: !!workflowId,
    queryFn: async () => {
      const res = await WorkflowsService.getWorkflow(workflowId ?? '');
      return res;
    },
  });

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: org?.name ? `/organization/${org?.id}/workflows` : '/organizations',
    },
    {
      label: 'Workflows',
      href: `/organization/${org?.id}/workflows_o`,
    },
    {
      label: workflow.data?.name || 'Workflow',
      href: `/organization/${org?.id}/workflows_o/${workflowId}`,
    },
    {
      label: 'Workflow Runs',
      href: `/organization/${org?.id}/workflows_o/${workflowId}/execution_details`,
    },
  ];

  return (
    <div>
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>
      {workflowExecutions.isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : !sortedWorkflowExecutions?.length ? (
        <div className="flex h-32 items-center justify-center text-muted-foreground">
          No workflow executions found
        </div>
      ) : (
        <div className="mt-6 rounded-md border shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="w-[200px]">Execution ID</TableHead>
                <TableHead className="w-[150px]">Run Type</TableHead>
                {workflow.data?.type === "BulkRun" && (
                  <TableHead className="w-[200px]">Account Name</TableHead>
                )}
                <TableHead className="w-[150px]">Status</TableHead>
                <TableHead className="w-[200px]">Started At</TableHead>
                <TableHead className="w-[200px]">Ended At</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentItems?.map((workflowExecution) => (
                <TableRow key={workflowExecution.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium">{workflowExecution.id}</TableCell>
                  <TableCell>
                    {workflow.data?.type === "BulkRun" ? "Bulk Run" : "Single Run"}
                  </TableCell>
                  {workflow.data?.type === "BulkRun" && (
                    <TableCell>
                      {workflowExecution.executionData?.accountName ?? 'N/A'}
                    </TableCell>
                  )}
                  <TableCell>
                    <span className={cn(
                      "inline-flex items-center rounded-full px-2 py-1 text-xs font-medium",
                      workflowExecution.status === "Completed" && "bg-green-100 text-green-700",
                      workflowExecution.status === "Failed" && "bg-red-100 text-red-700",
                      workflowExecution.status === "Running" && "bg-blue-100 text-blue-700",
                      workflowExecution.status === "Pending" && "bg-yellow-100 text-yellow-700"
                    )}>
                      {workflowExecution.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {workflowExecution.startedAtUtc ? new Date(workflowExecution.startedAtUtc).toLocaleString() : 'N/A'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {workflowExecution.endedAtUtc ? new Date(workflowExecution.endedAtUtc).toLocaleString() : 'N/A'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          
          {/* Pagination Controls */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Rows per page
              </p>
              <Select
                value={itemsPerPage.toString()}
                onValueChange={(value) => {
                  setItemsPerPage(Number(value));
                  setCurrentPage(1); // Reset to first page when changing items per page
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={itemsPerPage} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
